import { computed, reactive } from "@vue/composition-api";

import {
  HIGHLIGHT_DURATION,
  LAUNCH_REJECTION_CODES,
  SITE_UPDATED_CANCEL_FAILED_MESSAGE,
} from "./constants";
import { useCtcCreateFlow } from "./useCtcCreateFlow";

/**
 * Page-level state of the CTC assistant: loaded records, the creation flow
 * and the persisted RMR drafts.
 * No lifecycle hook is registered here: the page must call dispose().
 *
 * @param {Object} service - createCtcService() instance
 */
export function useCtcWorkspace(service) {
  const state = reactive({
    initializing: true,
    loadError: null,
    actionError: null,
    sites: [],
    siteSettings: [],
    siteSettingsLoading: false,
    siteSettingsTotal: 0,
    siteSettingsPage: 1,
    siteSettingsLimit: 50,
    savingSiteSettingIds: [],
    opportunities: [],
    opportunitiesLoading: false,
    opportunitiesTotal: 0,
    opportunitiesPage: 1,
    opportunitiesLimit: 10,
    articles: [],
    articlesLoading: false,
    articlesTotal: 0,
    articlesPage: 1,
    articlesLimit: 10,
    articlesSearch: undefined,
    highlightedArticleId: null,
    retryingIds: [],
    reschedulingIds: [],
    // The manager's Affilizz verifications not finalized (they survive reloads).
    pendingVerifications: [],
    abandoningVerificationIds: [],
  });

  let highlightTimer = null;
  let articleLoadToken = 0;
  let opportunityLoadToken = 0;
  let siteSettingsLoadToken = 0;
  let disposed = false;

  // =>  HELPERS
  const findOpportunity = (id) =>
    state.opportunities.find((item) => item.id === id) || null;

  const replaceOpportunity = (opportunity) => {
    const index = state.opportunities.findIndex(
      (item) => item.id === opportunity.id
    );
    if (index >= 0) state.opportunities.splice(index, 1, opportunity);
  };

  const replaceArticle = (article) => {
    const index = state.articles.findIndex((item) => item.id === article.id);
    if (index >= 0) state.articles.splice(index, 1, article);
  };

  /** The flow kept everything because its verification could not be abandoned. */
  const reportCancelFailure = () =>
    reportError({ message: createFlow.state.cancelError });

  const reportError = (error) => {
    if (disposed) return;
    state.actionError =
      (error && error.message) || "Une erreur inattendue est survenue.";
  };

  const createFlow = useCtcCreateFlow({ service });

  // =>  COMPUTED
  const opportunityStartBlockedReason = computed(() => {
    if (createFlow.state.launching) {
      return "Un brouillon est en cours de création : patientez quelques secondes.";
    }
    return null;
  });

  const highlightArticle = (id) => {
    clearTimeout(highlightTimer);
    state.highlightedArticleId = id;
    highlightTimer = setTimeout(() => {
      state.highlightedArticleId = null;
      highlightTimer = null;
    }, HIGHLIGHT_DURATION);
  };

  // =>  ACTIONS
  const loadArticles = async ({ page, limit, search } = {}) => {
    const token = ++articleLoadToken;
    state.articlesLoading = true;
    state.articlesSearch = search;
    try {
      const result = await service.listArticles({
        page: page || state.articlesPage,
        limit: limit || state.articlesLimit,
        search,
      });
      if (disposed || token !== articleLoadToken) return;
      state.articles = result.items;
      state.articlesTotal = result.total;
      state.articlesPage = result.page;
      state.articlesLimit = result.limit;
    } catch (error) {
      if (token === articleLoadToken) reportError(error);
    } finally {
      if (token === articleLoadToken) state.articlesLoading = false;
    }
  };

  const loadOpportunities = async ({ page, limit, search, shop } = {}) => {
    const token = ++opportunityLoadToken;
    state.opportunitiesLoading = true;
    try {
      const result = await service.listOpportunities({
        page: page || state.opportunitiesPage,
        limit: limit || state.opportunitiesLimit,
        search,
        shop,
      });
      if (disposed || token !== opportunityLoadToken) return;
      state.opportunities = result.items;
      state.opportunitiesTotal = result.total;
      state.opportunitiesPage = result.page;
      state.opportunitiesLimit = result.limit;
    } catch (error) {
      if (token === opportunityLoadToken) reportError(error);
    } finally {
      if (token === opportunityLoadToken) state.opportunitiesLoading = false;
    }
  };

  const loadSiteSettings = async ({ page, limit, search, readiness } = {}) => {
    const token = ++siteSettingsLoadToken;
    state.siteSettingsLoading = true;
    try {
      const result = await service.listSiteSettings({
        page: page || state.siteSettingsPage,
        limit: limit || state.siteSettingsLimit,
        search,
        readiness,
      });
      if (disposed || token !== siteSettingsLoadToken) return;
      state.siteSettings = result.items;
      state.siteSettingsTotal = result.total;
      state.siteSettingsPage = result.page;
      state.siteSettingsLimit = result.limit;
    } catch (error) {
      if (token === siteSettingsLoadToken) reportError(error);
    } finally {
      if (token === siteSettingsLoadToken) state.siteSettingsLoading = false;
    }
  };

  const updateSiteSetting = async ({ id, defaultSignature, isEnabled }) => {
    if (state.savingSiteSettingIds.includes(id)) return false;
    state.savingSiteSettingIds.push(id);
    try {
      const updated = await service.updateSiteSetting(id, {
        defaultSignature,
        isEnabled,
      });
      const index = state.siteSettings.findIndex((item) => item.id === id);
      if (index >= 0) state.siteSettings.splice(index, 1, updated);
      if (
        !updated.isEligible &&
        createFlow.state.siteId === id &&
        !(await createFlow.reset())
      ) {
        // Partial success: the site stays saved (no rollback, no retry) and
        // its verification is kept as is, so "nothing changed" would be false.
        createFlow.state.cancelError = SITE_UPDATED_CANCEL_FAILED_MESSAGE;
        reportError({ message: SITE_UPDATED_CANCEL_FAILED_MESSAGE });
        await loadPendingVerifications();
      }
      try {
        state.sites = await service.listSites();
      } catch (error) {
        reportError(error);
      }
      return true;
    } catch (error) {
      reportError(error);
      return false;
    } finally {
      state.savingSiteSettingIds = state.savingSiteSettingIds.filter(
        (item) => item !== id
      );
    }
  };

  const init = async () => {
    state.initializing = true;
    state.loadError = null;
    try {
      const [sites, opportunities, articles] = await Promise.all([
        service.listSites(),
        service.listOpportunities({ page: 1, limit: state.opportunitiesLimit }),
        service.listArticles({ page: 1, limit: state.articlesLimit }),
      ]);
      if (disposed) return;
      Object.assign(state, {
        sites,
        opportunities: opportunities.items,
        opportunitiesTotal: opportunities.total,
        opportunitiesPage: opportunities.page,
        opportunitiesLimit: opportunities.limit,
        articles: articles.items,
        articlesTotal: articles.total,
        articlesPage: articles.page,
        articlesLimit: articles.limit,
      });
    } catch (error) {
      state.loadError =
        (error && error.message) ||
        "Les données de l’assistant CTC n’ont pas pu être chargées.";
    } finally {
      state.initializing = false;
    }
    await loadPendingVerifications();
  };

  const startFromOpportunity = async (id) => {
    const opportunity = findOpportunity(id);
    if (
      !opportunity ||
      createFlow.state.launching ||
      opportunity.status !== "new"
    ) {
      return false;
    }

    if ((await createFlow.loadOpportunity(opportunity)) === null) {
      reportCancelFailure();
      return false;
    }
    return true;
  };

  /**
   * @returns {Promise<"started"|"current"|"confirm"|"blocked"|"unavailable">}
   *   "confirm": another product is in the flow, ask before replacing it.
   */
  const requestOpportunity = async (id) => {
    const opportunity = findOpportunity(id);
    if (!opportunity || opportunity.status !== "new") {
      return "unavailable";
    }
    if (createFlow.state.opportunityId === id) return "current";
    if (opportunityStartBlockedReason.value) return "blocked";
    if (createFlow.hasProduct.value) return "confirm";
    return (await startFromOpportunity(id)) ? "started" : "unavailable";
  };

  const loadPendingVerifications = async () => {
    try {
      const verifications = await service.listPendingVerifications();
      if (!disposed) state.pendingVerifications = verifications;
    } catch (error) {
      reportError(error);
    }
  };

  const resetCreateFlow = async () => {
    if (!(await createFlow.reset())) {
      reportCancelFailure();
      return;
    }
    await loadPendingVerifications();
  };

  /** Back to a pending verification: same draft, same Affilizz contents. */
  const resumeVerification = async (article) => {
    if (createFlow.state.launching) return;
    const site = state.sites.find(({ id }) => id === article.siteId);
    if (
      !(await createFlow.resumeVerification(
        article,
        site && site.defaultSignature
      ))
    ) {
      reportCancelFailure();
      return;
    }
    state.pendingVerifications = state.pendingVerifications.filter(
      (item) => item.id !== article.id
    );
  };

  /** Leaves a resumed verification without cancelling or changing it. */
  const leaveVerification = async () => {
    const article = createFlow.state.verification.article;
    if (!createFlow.leaveResumedVerification()) return;
    if (article) {
      state.pendingVerifications = [
        article,
        ...state.pendingVerifications.filter((item) => item.id !== article.id),
      ];
    }
    await loadPendingVerifications();
  };

  /**
   * Abandons a pending verification: its opportunity is released, its
   * Affilizz contents stay orphaned (no Affilizz deletion).
   */
  const abandonVerification = async (article) => {
    if (state.abandoningVerificationIds.includes(article.id)) return;
    state.abandoningVerificationIds.push(article.id);
    try {
      await service.cancelAffilizzCheck(article.requestId);
      createFlow.clearAbandonedVerification(article.requestId);
      await Promise.all([
        loadPendingVerifications(),
        loadOpportunities({
          page: state.opportunitiesPage,
          limit: state.opportunitiesLimit,
        }),
      ]);
    } catch (error) {
      reportError(error);
    } finally {
      state.abandoningVerificationIds = state.abandoningVerificationIds.filter(
        (id) => id !== article.id
      );
    }
  };

  /** @returns {Promise<Article|null>} */
  const launchArticle = async () => {
    if (!createFlow.canLaunch.value) return null;
    createFlow.state.launching = true;
    createFlow.state.launchError = null;
    try {
      const verified = createFlow.state.verification.article;
      const opportunityId = createFlow.state.opportunityId;
      const article = await service.finalizeArticle(
        verified.id,
        createFlow.buildFinalizeRequest()
      );
      if (disposed) return null;
      // Finalized: the reset below must not cancel it.
      createFlow.state.verification.article = article;
      // A replayed finalization returns the same draft: the server page and
      // total stay the only reliable count.
      await loadArticles({
        page: state.articlesPage,
        limit: state.articlesLimit,
        search: state.articlesSearch,
      });
      // Only the launch answer carries the generation state (blocked code).
      replaceArticle(article);
      // The backend already marked the opportunity as used with this draft.
      const opportunity = opportunityId ? findOpportunity(opportunityId) : null;
      if (opportunity) {
        replaceOpportunity({
          ...opportunity,
          status: "used",
          articleId: article.id,
        });
      }
      // Finalized just above: nothing is left to abandon.
      await createFlow.reset();
      highlightArticle(article.id);
      loadPendingVerifications();
      return article;
    } catch (error) {
      // Back to the Affilizz verification: the manager stays on the flow.
      if (error && LAUNCH_REJECTION_CODES.includes(error.code)) {
        if (!disposed) createFlow.state.launchError = error.message;
      } else {
        reportError(error);
      }
      return null;
    } finally {
      createFlow.state.launching = false;
    }
  };

  /** @returns {Promise<Article|null>} the draft with its generation state */
  const retryAffilizz = async (id) => {
    if (state.retryingIds.includes(id)) return null;
    state.retryingIds.push(id);
    try {
      const article = await service.retryAffilizz(id);
      replaceArticle(article);
      return article;
    } catch (error) {
      reportError(error);
      // The retry completed the contents without affiliation: the draft is gone.
      if (error && LAUNCH_REJECTION_CODES.includes(error.code)) {
        await loadArticles({
          page: state.articlesPage,
          limit: state.articlesLimit,
          search: state.articlesSearch,
        });
      }
      return null;
    } finally {
      state.retryingIds = state.retryingIds.filter((item) => item !== id);
    }
  };

  /**
   * Saves the new requested publication, then retries the hand-over to AIF.
   * @returns {Promise<Article|null>}
   */
  const rescheduleArticle = async (id, { postStatus, scheduledAt }) => {
    if (state.reschedulingIds.includes(id)) return null;
    state.reschedulingIds.push(id);
    try {
      replaceArticle(
        await service.updateArticlePublication(id, { postStatus, scheduledAt })
      );
    } catch (error) {
      reportError(error);
      return null;
    } finally {
      state.reschedulingIds = state.reschedulingIds.filter(
        (item) => item !== id
      );
    }
    return retryAffilizz(id);
  };

  const dismissError = () => {
    state.actionError = null;
  };

  const dispose = () => {
    disposed = true;
    clearTimeout(highlightTimer);
    highlightTimer = null;
    createFlow.dispose();
  };

  return {
    state,
    createFlow,
    opportunityStartBlockedReason,
    init,
    loadArticles,
    loadOpportunities,
    loadSiteSettings,
    requestOpportunity,
    startFromOpportunity,
    resetCreateFlow,
    loadPendingVerifications,
    resumeVerification,
    leaveVerification,
    abandonVerification,
    launchArticle,
    retryAffilizz,
    rescheduleArticle,
    updateSiteSetting,
    dismissError,
    dispose,
  };
}
